/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type TestPaneEntityPredictionProps = {
    /**
     * The predicted entity name.
     */
    entityName: string;

    /**
     * The subtext from the utterance that got predicted
     * with that entity.
     */
    utteranceSubtext: string;

    /**
     * The resolution of this utterance subtext if exists.
     */
    resolution?: string;

    /**
     * Indicates whether this item is highlighted.
     */
    isHighlighted?: boolean;
};

const messages = defineMessages({
    resolution: {
        id: 'testPane.testPaneEntityPrediction.resolution',
        defaultMessage: 'Resolution'
    }
});

const theme = getScopedTheme('TestPaneEntityPrediction');

const resolutionTitleStyles = classNamesFunction<ITextProps, ITextStyles>()({ root: { fontStyle: 'italic' } });

export const TestPaneEntityPrediction = (props: TestPaneEntityPredictionProps) => {
    const { entityName, utteranceSubtext, resolution, isHighlighted = false } = props;
    const { localize } = useLocalization();

    const titleColor = isHighlighted ? theme.highlightedTextColor : theme.titleColor;
    const informativeTextColor = isHighlighted ? theme.highlightedTextColor : theme.informativeTextColor;

    return (
        <Stack tokens={{ childrenGap: 4, padding: '6px 0' }}>
            <LuisText data-automation-id="entityName" variant="smallPlus" fontWeight="bold" color={titleColor}>
                {entityName}
            </LuisText>

            <LuisText data-automation-id="utteranceSubtext" variant="smallPlus" color={informativeTextColor}>
                {utteranceSubtext}
            </LuisText>

            {resolution && (
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <LuisText variant="smallPlus" color={titleColor} styles={resolutionTitleStyles}>
                        {localize(messages.resolution)}:
                    </LuisText>

                    <LuisText data-automation-id="resolution" variant="smallPlus" color={informativeTextColor}>
                        {resolution}
                    </LuisText>
                </Stack>
            )}
        </Stack>
    );
};
